// AdminPromptsPage.js — Phase PC.MOD.ADMIN
//
// /admin/prompts — recovery + audit surface for the community prompt catalog.
//
// Two tabs:
//   Published — every community-published prompt (excludes 'official' docs),
//               newest first. Each row: thumbnail + title + publisher + date.
//               Actions: Remove from gallery (soft-delete via
//               adminRemoveCommunityPrompt) + Ban publisher
//               (adminSetPublisherBan).
//
//   Rejected  — every moderationRejections doc, newest first. Shows what
//               OpenAI blocked, by whom. Useful for spotting:
//               - false-positive patterns (good prompts unfairly blocked)
//               - abuse patterns (one UID repeatedly hammering)
//               Action: Ban publisher.
//
// Admin gate: same ADMIN_UIDS allowlist the other /admin/* pages use
// (template-candidates, hub, etc.). Mutations go through admin-only Cloud
// Functions so server-side validates the admin role even if Firestore rules
// drift later.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

function formatDate(ts) {
  if (!ts) return '';
  // Accepts Firestore Timestamps (from direct reads), {seconds} maps (from
  // serialized snapshots), or ISO strings (from callable returns where the
  // server converts Timestamps to ISO for JSON safety).
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (typeof ts === 'string') d = new Date(ts);
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function PublisherChip({ name, uid, banned, onBan, onUnban, busy }) {
  const action = banned ? onUnban : onBan;
  const verb = banned ? 'Unban' : 'Ban';
  const busyVerb = banned ? 'Unbanning…' : 'Banning…';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, color: '#374151' }}>
        <span style={{ fontWeight: 600 }}>{name || '(no name)'}</span>{' '}
        <code style={{ fontSize: 11, color: '#9ca3af' }}>{uid.slice(0, 8)}…</code>
        {banned && (
          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px',
                         background: '#fee2e2', color: '#991b1b', borderRadius: 999 }}>BANNED</span>
        )}
      </span>
      <button onClick={action} disabled={busy}
        style={{ padding: '2px 8px', fontSize: 11, fontWeight: 600,
                 background: busy ? '#fef3c7' : (banned ? '#dcfce7' : '#fee2e2'),
                 color: banned ? '#166534' : '#991b1b',
                 border: '1px solid ' + (banned ? '#bbf7d0' : '#fecaca'),
                 borderRadius: 999, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? busyVerb : `${verb} publisher`}
      </button>
    </div>
  );
}

function PublishedRow({ product, banned, onRemove, onPurge, onBan, onUnban, busyAction }) {
  const isRemoved = product.removed === true;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '72px 1fr auto', gap: 14,
      padding: '12px 14px', alignItems: 'center',
      background: isRemoved ? '#fafafa' : '#fff',
      border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8,
      opacity: isRemoved ? 0.6 : 1,
    }}>
      <div style={{ width: 72, height: 72, background: '#f3f4f6', borderRadius: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {product.iconURL ? (
          <img src={product.iconURL} alt="" loading="lazy"
               style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ color: '#9ca3af', fontSize: 24 }}>♪</span>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
            {product.name || product.slug || '(no name)'}
          </span>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999,
                         background: '#eef2ff', color: '#4338ca', fontWeight: 600 }}>
            {product.subType || 'prompt'}
          </span>
          {isRemoved && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999,
                           background: '#fee2e2', color: '#991b1b', fontWeight: 600 }}>removed</span>
          )}
        </div>
        <div style={{ marginBottom: 6 }}>
          <PublisherChip name={product.publisherName} uid={product.publisherUid}
            banned={banned}
            onBan={() => onBan(product.publisherUid)}
            onUnban={() => onUnban(product.publisherUid)}
            busy={busyAction === `ban:${product.publisherUid}`} />
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Published {formatDate(product.publishedAt)} · {product.ratingCount || 0} ratings
          {product.averageRating > 0 && ` · ★ ${product.averageRating.toFixed(1)}`}
        </div>
        {product.prompt && (
          <div style={{ fontSize: 12, color: '#374151', marginTop: 6,
                        background: '#f9fafb', padding: '6px 8px', borderRadius: 6,
                        maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {product.prompt.slice(0, 240)}{product.prompt.length > 240 ? '…' : ''}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {!isRemoved && (
          <button onClick={() => onRemove(product)}
            disabled={busyAction === `remove:${product._id}`}
            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
                     background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca',
                     borderRadius: 8, cursor: busyAction === `remove:${product._id}` ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {busyAction === `remove:${product._id}` ? 'Removing…' : 'Remove'}
          </button>
        )}
        <button onClick={() => onPurge(product)}
          disabled={busyAction === `purge:${product._id}`}
          title="Hard delete: removes the Firestore doc AND the GCS preview file. Cannot be undone."
          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
                   background: '#991b1b', color: '#fff', border: '1px solid #7f1d1d',
                   borderRadius: 8, cursor: busyAction === `purge:${product._id}` ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
          {busyAction === `purge:${product._id}` ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  );
}

function RejectedRow({ rec, banned, onBan, onUnban, busyAction }) {
  return (
    <div style={{
      padding: '12px 14px', background: '#fff',
      border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        {(rec.categories || []).map((c) => (
          <span key={c} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999,
                                  background: '#fee2e2', color: '#991b1b', fontWeight: 700 }}>{c}</span>
        ))}
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999,
                       background: '#eef2ff', color: '#4338ca', fontWeight: 600 }}>{rec.appletId}</span>
        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto' }}>{formatDate(rec.flaggedAt)}</span>
      </div>
      <div style={{ marginBottom: 8 }}>
        <PublisherChip name={null} uid={rec.uid} banned={banned}
          onBan={() => onBan(rec.uid)} onUnban={() => onUnban(rec.uid)}
          busy={busyAction === `ban:${rec.uid}`} />
      </div>
      {rec.title && <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>{rec.title}</div>}
      {rec.prompt && (
        <div style={{ fontSize: 12, color: '#374151',
                      background: '#f9fafb', padding: '8px 10px', borderRadius: 6, lineHeight: 1.45 }}>
          {rec.prompt}
        </div>
      )}
      {Array.isArray(rec.tags) && rec.tags.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#6b7280' }}>tags: {rec.tags.join(', ')}</div>
      )}
    </div>
  );
}

export default function AdminPromptsPage() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('published');   // 'published' | 'rejected'
  const [showRemoved, setShowRemoved] = useState(false);
  const [published, setPublished] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [bannedUids, setBannedUids] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState('');   // "remove:productId" | "ban:uid" | ""
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    const off = getAuth().onAuthStateChanged((u) => setUser(u));
    return off;
  }, []);

  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      // Published list — endProducts is publicly readable so we can fetch
      // it directly. We filter publisherUid != 'official' client-side because
      // Firestore can't combine '!=' with the contentCategory '==' filter
      // efficiently for a tiny collection.
      const db = getFirestore();
      const promptQuery = query(
        collection(db, 'endProducts'),
        where('contentCategory', '==', 'prompt')
      );
      const psnap = await getDocs(promptQuery);
      const pubs = psnap.docs
        .map((d) => ({ ...d.data(), _id: d.id }))
        .filter((p) => p.publisherUid !== 'official')
        .sort((a, b) => (b.publishedAt?.seconds || 0) - (a.publishedAt?.seconds || 0));
      setPublished(pubs);

      // Rejected list + banned-uids set come from a single admin-gated
      // callable so we don't depend on the moderationRejections collection
      // being publicly readable (it isn't — it contains rejected content).
      const fn = httpsCallable(getFunctions(), 'adminListModerationRejections');
      const result = await fn({ limit: 100 });
      setRejected(result.data?.rejections || []);
      setBannedUids(new Set(result.data?.bannedUids || []));
    } catch (err) {
      setError(err.message || 'Load failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) reload();
  }, [isAdmin]);

  async function handleRemove(product) {
    if (!window.confirm(`Remove "${product.name || product.slug}" from the gallery?`)) return;
    setBusyAction(`remove:${product._id}`);
    setStatusMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminRemoveCommunityPrompt');
      const result = await fn({ productId: product._id });
      setStatusMsg(result.data?.message || 'Removed.');
      await reload();
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  async function handleBan(uid) {
    if (!window.confirm(`Ban publisher ${uid.slice(0, 8)}… from posting new prompts? Existing prompts stay; use Remove or Delete to take those down.`)) return;
    setBusyAction(`ban:${uid}`);
    setStatusMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminSetPublisherBan');
      const result = await fn({ uid, banned: true });
      setBannedUids((prev) => new Set([...prev, uid]));
      setStatusMsg(result.data?.message || 'Banned.');
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  async function handleUnban(uid) {
    if (!window.confirm(`Unban publisher ${uid.slice(0, 8)}…? They will be able to publish new prompts again.`)) return;
    setBusyAction(`ban:${uid}`);
    setStatusMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminSetPublisherBan');
      const result = await fn({ uid, banned: false });
      setBannedUids((prev) => {
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
      setStatusMsg(result.data?.message || 'Unbanned.');
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  async function handlePurge(product) {
    if (!window.confirm(
      `PERMANENTLY DELETE "${product.name || product.slug}"?\n\n` +
      `This removes the Firestore doc AND the GCS preview file. It cannot be undone. ` +
      `Use "Remove" for reversible soft-delete instead.`
    )) return;
    setBusyAction(`purge:${product._id}`);
    setStatusMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminPurgeCommunityPrompt');
      const result = await fn({ productId: product._id });
      setStatusMsg(result.data?.message || 'Permanently deleted.');
      await reload();
    } catch (err) {
      setStatusMsg(`Error: ${err.message || err}`);
    } finally {
      setBusyAction('');
    }
  }

  const publishedVisible = useMemo(() =>
    showRemoved ? published : published.filter((p) => p.removed !== true),
  [published, showRemoved]);

  if (!user) {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '0 auto', textAlign: 'center', color: '#6b7280' }}>
        <p>Sign in to access admin tools.</p>
        <Link to="/account">Sign in</Link>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '0 auto', textAlign: 'center', color: '#6b7280' }}>
        <p>This page is restricted to administrators.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 64px' }}>
      <header style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 26, color: '#111827' }}>Admin · Prompts</h1>
        <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 13, maxWidth: 720 }}>
          Recovery + audit surface for the community prompt catalog. Community publishes
          auto-clear OpenAI's moderation API at submit time; this page surfaces the
          rejection log for false-positive monitoring and lets you remove or ban after
          the fact when needed.
        </p>
      </header>

      <nav style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { id: 'published', label: `Published (${published.filter(p => p.removed !== true).length})` },
          { id: 'rejected',  label: `Rejected (${rejected.length})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '8px 14px', borderRadius: 999,
                     background: tab === t.id ? '#111827' : '#fff',
                     color: tab === t.id ? '#fff' : '#374151',
                     border: '1px solid ' + (tab === t.id ? '#111827' : '#e5e7eb'),
                     fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={reload} disabled={loading}
          style={{ padding: '6px 12px', fontSize: 12, background: '#fff',
                   border: '1px solid #e5e7eb', borderRadius: 6, cursor: loading ? 'wait' : 'pointer' }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </nav>

      {tab === 'published' && (
        <div style={{ marginBottom: 12, fontSize: 12, color: '#6b7280' }}>
          <label style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
            {' '}Show removed
          </label>
        </div>
      )}

      {error && (
        <div style={{ padding: 12, marginBottom: 12, background: '#fef2f2',
                      color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{error}</div>
      )}
      {statusMsg && (
        <div style={{ padding: 12, marginBottom: 12, background: '#eff6ff',
                      color: '#1e40af', borderRadius: 6, fontSize: 13 }}>{statusMsg}</div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
      ) : tab === 'published' ? (
        publishedVisible.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14,
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
            No community publishes yet. (The 92 official prompts are not listed here.)
          </div>
        ) : (
          publishedVisible.map((p) => (
            <PublishedRow key={p._id} product={p}
              banned={bannedUids.has(p.publisherUid)}
              onRemove={handleRemove} onPurge={handlePurge}
              onBan={handleBan} onUnban={handleUnban}
              busyAction={busyAction} />
          ))
        )
      ) : (
        rejected.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14,
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
            No rejections logged. Either nothing has been blocked or the
            moderationRejections collection is fresh (logging started with this deploy).
          </div>
        ) : (
          rejected.map((r) => (
            <RejectedRow key={r._id} rec={r}
              banned={bannedUids.has(r.uid)}
              onBan={handleBan} onUnban={handleUnban}
              busyAction={busyAction} />
          ))
        )
      )}
    </div>
  );
}
